import React from 'react';
import Toaster from '@amzn/meridian/toaster';
import Alert from '@amzn/meridian/alert';
import { ToasterToasts } from '@amzn/meridian/toaster/toaster';
import { createContext, ReactNode, useContext, useState } from 'react';
import { AlertType } from '@amzn/meridian/alert/alert';

/**
 * Properties required to create a toast
 */
export interface ToastProps extends Partial<ToasterToasts> {
  /**
   * Child object to display within an {@link Alert}.
   */
  readonly children: ReactNode;
  /**
   * Type of the alert ({@link AlertType})
   */
  readonly alertType: AlertType;
  /**
   * ID of the toast. Defaults to a random number.
   */
  readonly id?: string;
  /**
   * If present, the toast will automatically time out after this delay. If not, the user must manually dismiss it.
   */
  readonly timeout?: number;
}

/**
 * Internal type that extends {@link ToasterToasts} (and thus has a required id property), plus the non-overlapping
 * properties from ToastProps.
 */
type Toast = ToasterToasts & Exclude<ToastProps, keyof ToasterToasts>;

/**
 * Interface for all items in the ToastContext.
 */
interface ToastContextInterface extends ToasterInterface {
  /**
   * Array of currently active toasts.
   */
  toasts: Toast[];
}

/**
 * Narrower interface for items that we want to expose via the {@link useToaster} hook.
 */
interface ToasterInterface {
  /**
   * Create a new toast.
   * @param toast Properties for the new toast
   */
  openToast: (toast: ToastProps) => void;
  /**
   * Close a specific toast.
   * @param id ID that the toast was created with
   */
  closeToast: (id: string) => void;
}

/**
 * React context containing the list of active toasts and functions for modifying it.
 */
const ToastContext = createContext<ToastContextInterface>({
  toasts: [],
  openToast: () => undefined,
  closeToast: () => undefined,
});

/**
 * Default duration for toasts, in ms.
 */
export const DEFAULT_TOAST_TIMEOUT = 10000;

/**
 * Creates a ContextProvider for toasts.
 * @param children Children elements
 * @constructor
 */
export function ToastContextProvider({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const openToast = (toast: ToastProps) => {
    const toastWithDefaults = {
      timeout: DEFAULT_TOAST_TIMEOUT,
      ...toast,
    };
    setToasts([
      ...toasts,
      { id: Math.random().toString(), ...toastWithDefaults },
    ]);
  };
  const closeToast = (id: string) =>
    setToasts(toasts.filter(toast => toast.id !== id));

  return (
    <ToastContext.Provider value={{ toasts, openToast, closeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

/**
 * Hook that should be used in any component that wants to generate toasts. Typical usage will be:
 * ```
 * const { openToast } = useToaster();
 * ```
 *
 * (the closeToast method is also available from the return, but is not likely to be used in most cases).
 * @returns functions for opening/closing toasts
 */
export function useToaster(): ToasterInterface {
  const { openToast, closeToast } = useContext(ToastContext);
  return { openToast, closeToast };
}

/**
 * Component that will display toasts. This makes use of Meridian's {@link Toaster}, and uses {@link Alert} to render
 * toasts.
 * @constructor
 */
export function AppToaster(): React.JSX.Element {
  const { toasts, closeToast } = useContext(ToastContext);
  return (
    <Toaster toasts={toasts} onCloseToast={closeToast}>
      {(toast: Toast) => (
        <Alert
          toast={true}
          type={toast.alertType}
          onClose={() => closeToast(toast.id)}
        >
          {toast.children}
        </Alert>
      )}
    </Toaster>
  );
}
